import React, { useState, useEffect, useRef } from 'react';
import zen_logo from '@/assets/images/zen.png';
import {
  Menu,
  X,
  Sun,
  Moon,
  CheckCircle,
  ArrowRight,
  Sparkles,
  BookOpen,
  Target,
  Users,
  BarChart2,
  MessageSquare,
  FileText,
  Twitter,
  Linkedin,
  Instagram,
  ChevronDown,
  Building,
  Mail,
  Phone,
  User,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Axios from '@/services/axios';

// --- SVG Components ---
const OwlLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 448 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M224 48C195.5 48 169.3 56.8 146.9 72.4L144.5 74.2C142.1 75.9 139.3 77.1 136.3 77.4C133.4 77.7 130.4 77.1 127.9 75.8L120.3 71.8C111.3 67.2 101.1 64 90.5 64C40.5 64 0 104.5 0 154.5C0 183.1 11.3 209.3 30.1 228.1L32.6 230.6C34.3 232.3 35.5 234.5 35.8 236.9C36.1 239.2 35.5 241.6 34.2 243.7L30.2 251.3C25.6 260.3 22.5 270.5 22.5 281.1C22.5 331.1 63 371.6 113 371.6C123.6 371.6 133.8 368.5 142.8 363.9L150.4 359.9C152.5 358.6 155 358 157.4 358.3C159.8 358.6 162.1 359.7 163.8 361.4L166.3 363.9C185.1 382.7 211.3 394 240 394C290 394 330.5 353.5 330.5 303.5C330.5 292.9 327.4 282.7 322.8 273.7L318.8 266.1C317.5 264 316.9 261.6 317.2 259.2C317.5 256.9 318.7 254.7 320.4 253L322.9 250.5C341.7 231.7 353 205.5 353 176.9C353 126.9 312.5 86.4 262.5 86.4C251.9 86.4 241.7 89.5 232.7 94.1L225.1 98.1C222.6 99.4 219.6 100 216.7 99.7C213.7 99.4 210.9 98.2 208.5 96.5L206.1 94.7C183.7 79.1 157.5 70.3 134.5 70.3L144.5 74.2C146.9 75.9 149.7 77.1 152.7 77.4C155.6 77.7 158.6 77.1 161.1 75.8L168.7 71.8C177.7 67.2 187.9 64 198.5 64H224zM113 447.6C50.6 447.6 0 397.1 0 334.6C0 324 3.1 313.8 8.7 304.8L12.7 297.2C14 295.1 14.6 292.7 14.3 290.3C14 287.9 12.8 285.7 11.1 284L8.6 281.5C-10.2 262.7 -22.5 236.5 -22.5 207.9C-22.5 108.9 58 28.4 157.5 28.4C168.1 28.4 178.3 31.5 187.3 36.1L194.9 40.1C197.4 41.4 200.4 42 203.3 41.7C206.3 41.4 209.1 40.2 211.5 38.5L213.9 36.7C236.3 21.1 262.5 12.3 285.5 12.3C335.5 12.3 376 52.8 376 102.8C376 131.4 364.7 157.6 345.9 176.4L343.4 178.9C341.7 180.6 340.5 182.8 340.2 185.2C339.9 187.5 340.5 189.9 341.8 192L345.8 199.6C350.4 208.6 353.5 218.8 353.5 229.4C353.5 279.4 313 319.9 263 319.9C252.4 319.9 242.2 316.8 233.2 312.2L225.6 308.2C223.1 306.9 220.1 306.3 217.2 306.6C214.2 306.9 211.4 308.1 209 309.8L206.6 311.6C184.2 327.2 158 336 134.5 336H113C113 397.1 163.6 447.6 226 447.6H448V512H224C224 512 224 447.6 113 447.6z" />
  </svg>
);

// --- Helper Hooks & Components ---

const useOnClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

const useScrollPosition = () => {
  const [scrollPosition, setScrollPosition] = useState(0);
  useEffect(() => {
    const updatePosition = () => setScrollPosition(window.pageYOffset);
    window.addEventListener('scroll', updatePosition);
    updatePosition();
    return () => window.removeEventListener('scroll', updatePosition);
  }, []);
  return scrollPosition;
};

const Section = ({ children, className, ...props }) => {
  const ref = useRef();
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);
  return (
    <section
      ref={ref}
      className={`transition-opacity duration-1000 ease-in ${isVisible ? 'opacity-100' : 'opacity-0 translate-y-5'} ${
        className || ''
      }`}
      {...props}>
      {children}
    </section>
  );
};

// --- Main Page Components ---

const Header = ({ setMenuOpen, theme, toggleTheme, onDemoRequest }) => {
  const scrollPosition = useScrollPosition();
  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrollPosition > 50 ? 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg shadow-sm' : 'bg-transparent'
      }`}>
      <div className="container mx-auto px-4 sm:px-6 py-3">
        <nav className="flex justify-between items-center" aria-label="Main navigation">
          <a
            href="#"
            className="flex items-center gap-2 text-2xl font-bold text-slate-900 dark:text-white"
            style={{ fontFamily: "'DM Serif Display', serif" }}>
            <img src={zen_logo} alt="" className="size-9 rounded-lg" />
            ZenshKool
          </a>
          <div className="hidden md:flex space-x-6 items-center">
            <a
              href="#features"
              className="text-slate-700 dark:text-slate-300 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors font-medium">
              Features
            </a>
            <a
              href="#whitelabel"
              className="text-slate-700 dark:text-slate-300 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors font-medium">
              White-Label
            </a>
            <a
              href="#testimonials"
              className="text-slate-700 dark:text-slate-300 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors font-medium">
              Testimonials
            </a>
            <a
              href="#faq"
              className="text-slate-700 dark:text-slate-300 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors font-medium">
              FAQ
            </a>
          </div>
          <div className="hidden md:flex items-center space-x-4">
            <button
              onClick={onDemoRequest}
              className="bg-emerald-500 text-white px-5 py-2 rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-all duration-300 font-semibold shadow-sm hover:shadow-md transform hover:-translate-y-px active:translate-y-0">
              Request Demo
            </button>
            <button
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              className="p-2 rounded-full text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
          <div className="md:hidden flex items-center space-x-2">
            <button
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              className="p-2 rounded-full text-slate-500 dark:text-slate-400">
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="text-slate-800 dark:text-white p-2">
              <Menu />
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
};

const MobileMenu = ({ isOpen, setMenuOpen, onDemoRequest }) => {
  const menuRef = useRef();
  useOnClickOutside(menuRef, () => setMenuOpen(false));
  const handleDemoClick = () => {
    onDemoRequest();
    setMenuOpen(false);
  };
  return (
    <div
      className={`fixed inset-0 z-[100] transition-opacity duration-300 ease-in-out md:hidden ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}>
      <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOpen(false)}></div>
      <div
        ref={menuRef}
        className={`absolute top-0 right-0 h-full w-72 bg-white dark:bg-slate-900 shadow-xl transition-transform duration-500 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
        <div className="p-6 flex flex-col h-full">
          <button
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
            className="self-end mb-8 text-slate-600 dark:text-slate-300">
            <X size={24} />
          </button>
          <nav className="flex flex-col space-y-6 text-lg">
            <a onClick={() => setMenuOpen(false)} href="#features" className="text-slate-800 dark:text-slate-200">
              Features
            </a>
            <a onClick={() => setMenuOpen(false)} href="#whitelabel" className="text-slate-800 dark:text-slate-200">
              White-Label
            </a>
            <a onClick={() => setMenuOpen(false)} href="#testimonials" className="text-slate-800 dark:text-slate-200">
              Testimonials
            </a>
            <a onClick={() => setMenuOpen(false)} href="#faq" className="text-slate-800 dark:text-slate-200">
              FAQ
            </a>
          </nav>
          <button
            onClick={handleDemoClick}
            className="mt-auto w-full bg-emerald-500 text-white text-center px-6 py-3 rounded-lg font-semibold">
            Request a Demo
          </button>
        </div>
      </div>
    </div>
  );
};

const Hero = ({ onDemoRequest }) => {
  return (
    <section className="relative pt-32 pb-20 md:pt-48 md:pb-28 text-center overflow-hidden">
      <div className="absolute inset-0 bg-white dark:bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-b from-white to-transparent dark:from-slate-900 dark:to-transparent" />
      </div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60rem] h-[60rem] bg-emerald-400/10 dark:bg-emerald-400/10 rounded-full blur-3xl" />
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-3xl mx-auto">
          <h1
            className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white leading-tight"
            style={{ fontFamily: "'DM Serif Display', serif" }}>
            Empower Your Institution with a Branded AI Learning Platform
          </h1>
          <p className="mt-6 text-lg text-slate-600 dark:text-slate-300">
            Give your students a fully branded, AI-powered learning experience — with instant doubt resolution,
            personalized roadmaps, and advanced analytics for your faculty.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={onDemoRequest}
              className="w-full sm:w-auto inline-flex items-center justify-center bg-emerald-500 text-white text-lg font-semibold px-8 py-3 rounded-full hover:bg-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 active:translate-y-0">
              Request a Demo
              <ArrowRight className="ml-2" size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

const InstitutionsTrustBar = () => (
  <div className="py-12 bg-slate-50 dark:bg-slate-900">
    <div className="container mx-auto px-4 sm:px-6">
      <p className="text-center text-sm text-slate-500 dark:text-slate-400 font-medium tracking-wider uppercase">
        TRUSTED BY LEADING EDUCATIONAL INSTITUTIONS
      </p>
      <div className="flex justify-center items-center flex-wrap gap-x-12 gap-y-4 mt-6 grayscale opacity-60 dark:opacity-40 dark:invert filter blur-sm">
        <img
          src="https://raw.githubusercontent.com/Loopple/loopple-public-assets/main/motion-tailwind/img/logos/logo-1.svg"
          alt="Institution Logo 1"
          className="h-9"
        />
        <img
          src="https://raw.githubusercontent.com/Loopple/loopple-public-assets/main/motion-tailwind/img/logos/logo-5.svg"
          alt="Institution Logo 2"
          className="h-9"
        />
        <img
          src="https://raw.githubusercontent.com/Loopple/loopple-public-assets/main/motion-tailwind/img/logos/logo-3.svg"
          alt="Institution Logo 3"
          className="h-9"
        />
        <img
          src="https://raw.githubusercontent.com/Loopple/loopple-public-assets/main/motion-tailwind/img/logos/logo-4.svg"
          alt="Institution Logo 4"
          className="h-9"
        />
      </div>
      <p className="text-center text-xs text-slate-400 dark:text-slate-500 mt-2">Partner names redacted for privacy</p>
    </div>
  </div>
);

const Features = () => {
  const featuresData = [
    {
      icon: Target,
      title: 'AI-Personalized Roadmaps',
      description: "Our AI crafts a unique learning path, adapting to your students' strengths and weaknesses.",
    },
    {
      icon: MessageSquare,
      title: 'AI Chatbot per Course',
      description:
        'A personal tutor for every student, providing instant, intelligent answers to their questions 24/7.',
    },
    {
      icon: FileText,
      title: 'AI Summary Generator',
      description: 'Condense long notes and lectures into concise summaries for quick revision and better retention.',
    },
    {
      icon: Users,
      title: 'Branded Community',
      description: 'Foster collaboration within your institution with a dedicated, secure community for each course.',
    },
    {
      icon: BookOpen,
      title: 'Dynamic Quizzes & Flashcards',
      description: 'Engage students with quizzes that evolve based on their performance to master every concept.',
    },
    {
      icon: BarChart2,
      title: 'Institutional Analytics',
      description: 'Track student progress with detailed analytics and insights to inform your teaching strategies.',
    },
  ];

  return (
    <Section id="features" className="py-20 md:py-28 bg-white dark:bg-slate-950">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2
            className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white"
            style={{ fontFamily: "'DM Serif Display', serif" }}>
            A Smarter Platform for Your Students
          </h2>
          <p className="text-slate-700 dark:text-slate-300 mt-4 text-lg">
            Experience features designed for the next generation of educational excellence.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuresData.map((feature, index) => (
            <div
              key={index}
              className="feature-card bg-white dark:bg-slate-800 p-8 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 dark:border-slate-700"
              style={{ '--stagger-delay': `${index * 100}ms` }}>
              <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/50 text-emerald-500 dark:text-emerald-400 mb-6 transition-transform duration-300 icon-container">
                <feature.icon size={28} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{feature.title}</h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

const WhiteLabelSection = ({ onDemoRequest }) => (
  <Section id="whitelabel" className="py-20 md:py-28 bg-slate-50 dark:bg-slate-900">
    <div className="container mx-auto px-4 sm:px-6">
      <div className="text-center max-w-3xl mx-auto">
        <h2
          className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white"
          style={{ fontFamily: "'DM Serif Display', serif" }}>
          For Schools & Institutions: Your Own Branded Platform
        </h2>
        <p className="text-slate-700 dark:text-slate-300 mt-4 text-lg">
          We offer fully white-labeled versions of ZenshKool customized with your school’s name, colors, and domain —
          giving your students access to cutting-edge AI learning under your brand.
        </p>
        <div className="mt-8">
          <button
            onClick={onDemoRequest}
            className="inline-flex items-center justify-center bg-emerald-500 text-white text-lg font-semibold px-8 py-3 rounded-full hover:bg-emerald-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 active:translate-y-0">
            Request a Demo
          </button>
        </div>
      </div>
    </div>
  </Section>
);

const Testimonials = () => {
  const testimonialsData = [
    {
      name: 'Emily Carter',
      rank: 'STEM Coordinator, Larchview Secondary School, Canada',
      quote:
        '"ZenshKool has transformed how we approach student performance data. Teachers now spend less time on admin work and more time tailoring learning. It’s revolutionizing classroom dynamics."',
      img: 'https://i.pravatar.cc/150?img=12',
    },
    {
      name: 'Ayo Okonkwo',
      rank: 'University Student, Lagos, Nigeria',
      quote:
        '"Balancing part-time work and studies isn’t easy. ZenshKool’s AI tutor helped me master algorithms and statistics on my own time. It’s like having a personal coach in your pocket."',
      img: 'https://i.pravatar.cc/150?img=35',
    },
    {
      name: 'Mei Lin',
      rank: 'Middle School Teacher, Guangyuan District, China',
      quote:
        '"ZenshKool’s adaptive lessons and real-time feedback keep my students engaged even after school. It’s helped boost both their confidence and results."',
      img: 'https://i.pravatar.cc/150?img=68',
    },
    {
      name: 'Jackson Rivera',
      rank: 'High School Senior, São Paulo, Brazil',
      quote:
        '"The AI flashcards helped me prepare for university entrance exams more efficiently than any textbook. I could revise smarter, not harder."',
      img: 'https://i.pravatar.cc/150?img=22',
    },
    {
      name: 'Naledi Mokoena',
      rank: 'Parent, Mbabane, Eswatini',
      quote:
        '"My daughter used to struggle in science. With ZenshKool’s personalized learning path, she now looks forward to her classes. Her marks have improved drastically."',
      img: 'https://i.pravatar.cc/150?img=49',
    },
    {
      name: 'Liam O’Reilly',
      rank: 'Student, Dún Laoghaire College, Ireland',
      quote:
        '"Studying for my final year has never been this efficient. The instant feedback on essays helped me sharpen my arguments and time management."',
      img: 'https://i.pravatar.cc/150?img=14',
    },
    {
      name: 'Haruto Watanabe',
      rank: 'Math Teacher, Kyosei Junior School, Japan',
      quote:
        '"ZenshKool seamlessly integrates into our curriculum. It helps me identify students who need extra support, without the guesswork."',
      img: 'https://i.pravatar.cc/150?img=66',
    },
    {
      name: 'Zanele Dlamini',
      rank: 'Vocational Student, Gaborone, Botswana',
      quote:
        '"I joined ZenshKool to improve my English writing, and it exceeded expectations. The feedback is spot-on, and I feel much more confident now."',
      img: 'https://i.pravatar.cc/150?img=29',
    },
  ];

  const duplicatedTestimonials = [...testimonialsData, ...testimonialsData];

  return (
    <Section id="testimonials" className="py-20 md:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2
            className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white"
            style={{ fontFamily: "'DM Serif Display', serif" }}>
            Trusted by Educators and Students
          </h2>
        </div>
      </div>
      <div className="relative">
        <div className="testimonial-marquee group">
          <div className="flex animate-marquee group-hover:[animation-play-state:paused]">
            {duplicatedTestimonials.map((testimonial, index) => (
              <div key={index} className="flex-shrink-0 w-[90vw] max-w-md mx-4">
                <div className="bg-slate-50 dark:bg-slate-800 p-8 rounded-xl h-full flex flex-col justify-center transition-all duration-300 group-hover:opacity-60 hover:!opacity-100 hover:scale-105">
                  <p className="text-slate-700 dark:text-slate-200 mb-6 italic text-lg text-center">
                    “{testimonial.quote}”
                  </p>
                  <div className="flex items-center justify-center">
                    <img
                      src={testimonial.img}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full mr-4 object-cover"
                      loading="lazy"
                    />
                    <div>
                      <h4 className="font-bold text-slate-900 dark:text-white">{testimonial.name}</h4>
                      <p className="text-sm font-medium text-emerald-500 dark:text-emerald-400">{testimonial.rank}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};

const AccordionItem = ({ faq, index, openIndex, setOpenIndex }) => {
  const isOpen = index === openIndex;
  return (
    <div className="border-b border-slate-200 dark:border-slate-700 py-6">
      <button
        className="w-full flex justify-between items-center text-left text-lg font-semibold text-slate-800 dark:text-slate-100"
        onClick={() => setOpenIndex(isOpen ? null : index)}
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${index}`}>
        <span>{faq.question}</span>
        <span className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown />
        </span>
      </button>
      <div
        id={`faq-answer-${index}`}
        className="grid overflow-hidden transition-all duration-500 ease-in-out"
        style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}>
        <div className="min-h-0">
          <p className="pt-4 text-slate-600 dark:text-slate-300 leading-relaxed">{faq.answer}</p>
        </div>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const faqData = [
    {
      question: 'How does the white-labeling process work?',
      answer:
        "We work closely with you to customize the platform with your institution's branding, including logo, colors, and domain. Our team handles the entire setup process, ensuring a seamless experience for your students and faculty.",
    },
    {
      question: 'What kind of support do you offer for institutions?',
      answer:
        'We provide dedicated onboarding, training for your staff, and priority technical support to ensure a smooth integration and successful adoption of the platform within your institution.',
    },
    {
      question: 'Is the platform secure and compliant with student data privacy laws?',
      answer:
        'Yes, security and privacy are our top priorities. Our platform is built with robust security measures and is compliant with major data privacy regulations to protect student information.',
    },
    {
      question: 'Do you offer pilot programs?',
      answer:
        'Yes, we offer pilot programs for qualifying institutions. This allows you to test the platform with a select group of students and faculty to see the benefits firsthand before a full-scale implementation. Please request a demo to learn more.',
    },
  ];

  return (
    <Section id="faq" className="py-20 md:py-28 bg-white dark:bg-slate-950">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2
            className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white"
            style={{ fontFamily: "'DM Serif Display', serif" }}>
            Frequently Asked Questions
          </h2>
        </div>
        <div className="max-w-3xl mx-auto">
          {faqData.map((faq, index) => (
            <AccordionItem key={index} faq={faq} index={index} openIndex={openIndex} setOpenIndex={setOpenIndex} />
          ))}
        </div>
      </div>
    </Section>
  );
};

const Footer = () => {
  return (
    <footer className="border-t border-slate-200 dark:border-slate-800">
      <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 text-slate-600 dark:text-slate-300">
          <div className="md:col-span-12 lg:col-span-5">
            <h3
              className="text-2xl font-bold text-slate-900 dark:text-white mb-2"
              style={{ fontFamily: "'DM Serif Display', serif" }}>
              ZenshKool
            </h3>
            <p className="mb-4 max-w-md">Empowering institutions with the future of AI-driven education.</p>
            <div className="flex space-x-4 mt-4">
              <a href="#" aria-label="Instagram" className="text-slate-500 hover:text-emerald-500">
                <Instagram />
              </a>
              <a
                href="https://x.com/zenshkool_ai"
                aria-label="Twitter"
                className="text-slate-500 hover:text-emerald-500">
                <Twitter />
              </a>
              <a
                href="https://www.linkedin.com/company/zenshkool-ai/posts/?feedView=all"
                aria-label="LinkedIn"
                className="text-slate-500 hover:text-emerald-500">
                <Linkedin />
              </a>
            </div>
          </div>
          <div className="md:col-span-4 lg:col-span-2">
            <h4 className="font-semibold text-slate-900 dark:text-white mb-4">Platform</h4>
            <ul className="space-y-2">
              <li>
                <a href="#features" className="hover:text-emerald-500">
                  Features
                </a>
              </li>
              <li>
                <a href="#whitelabel" className="hover:text-emerald-500">
                  White-Label
                </a>
              </li>
              <li>
                <a href="#testimonials" className="hover:text-emerald-500">
                  Testimonials
                </a>
              </li>
            </ul>
          </div>
          <div className="md:col-span-4 lg:col-span-2">
            <h4 className="font-semibold text-slate-900 dark:text-white mb-4">Company</h4>
            <ul className="space-y-2">
              <li>
                <a href="/about" className="hover:text-emerald-500">
                  About Us
                </a>
              </li>
              <li>
                <Link href="#" className="hover:text-emerald-500">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/help" className="hover:text-emerald-500">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 dark:border-slate-800 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center text-sm text-slate-500">
          <p>&copy; {new Date().getFullYear()} ZenshKool AI Pvt. Ltd. All rights reserved.</p>
          <div className="flex space-x-4 mt-4 sm:mt-0">
            <Link to="/terms-conditions" className="hover:text-emerald-500">
              Terms of Service
            </Link>
            <Link to="/privacy-policies" className="hover:text-emerald-500">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

const StickySignupCTA = ({ onDemoRequest }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const toggleVisibility = () => {
      const section = document.getElementById('whitelabel');
      if (section && window.scrollY > 300 && window.scrollY < section.offsetTop - window.innerHeight) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    };
    window.addEventListener('scroll', toggleVisibility, { passive: true });
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-40 transition-all duration-500 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
      }`}>
      <button
        onClick={onDemoRequest}
        className="bg-slate-900/80 dark:bg-white/90 backdrop-blur-lg text-white dark:text-slate-900 flex items-center justify-center font-semibold px-6 py-3 rounded-full shadow-lg hover:scale-105 transition-transform border border-slate-700/50 dark:border-white/20">
        <Sparkles className="mr-2 text-emerald-400" size={20} />
        Request a Demo for Your School
      </button>
    </div>
  );
};

const DemoRequestModal = ({ isOpen, onClose }) => {
  const modalRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null); // 'success', 'error'
  useOnClickOutside(modalRef, onClose);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionStatus(null);

    try {
      const { data } = await Axios.post('/contact/demo', {
        name: e.target.name.value,
        school: e.target.school.value,
        email: e.target.email.value,
        phone: e.target.phone.value,
      });
      console.log(data);
      setSubmissionStatus('success');
    } catch (error) {
      setSubmissionStatus('failed');
    }

    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
      <div ref={modalRef} className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-8 w-full max-w-md">
        {submissionStatus === 'success' ? (
          <div className="text-center">
            <CheckCircle className="mx-auto h-16 w-16 text-emerald-500" />
            <h2 className="mt-4 text-2xl font-bold text-slate-900 dark:text-white">Thank You!</h2>
            <p className="mt-2 text-slate-600 dark:text-slate-300">
              Your demo request has been submitted. We'll be in touch shortly.
            </p>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-6">
              <h2
                className="text-2xl font-bold text-slate-900 dark:text-white"
                style={{ fontFamily: "'DM Serif Display', serif" }}>
                Request a Demo
              </h2>
              <button onClick={onClose} className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
                <X />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      name="name"
                      type="text"
                      id="name"
                      required
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="school" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    School / Institution Name
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      name="school"
                      type="text"
                      id="school"
                      required
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      name="email"
                      type="email"
                      id="email"
                      required
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      name="phone"
                      type="tel"
                      id="phone"
                      className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-500 text-white font-semibold py-3 rounded-lg hover:bg-emerald-600 transition-colors disabled:bg-emerald-400 disabled:cursor-not-allowed">
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// --- Main App Component ---

export default function App() {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isDemoModalOpen, setDemoModalOpen] = useState(false);
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = savedTheme || (prefersDark ? 'dark' : 'light');
    setTheme(initialTheme);
  }, []);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const handleDemoRequest = () => {
    // Analytics hook for tracking demo requests
    console.log('Analytics: Demo Requested');
    setDemoModalOpen(true);
  };

  const pageStyles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Inter:wght@400;500;600;700;800&display=swap');
    :root { --emerald-500: #10b981; }
    html { scroll-behavior: smooth; }
    body { font-family: 'Inter', sans-serif; background-color: #FFFFFF; color: #1e293b; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
    .dark body { background-color: #020617; color: #e2e8f0; }
    .feature-card { transition: transform 0.3s ease, box-shadow 0.3s ease; animation: slide-up 0.5s ease-out var(--stagger-delay, 0s) backwards; }
    .feature-card:hover { transform: scale(1.05) translateY(-6px) rotate(-1deg); }
    .feature-card:hover .icon-container { transform: rotate(10deg) scale(1.1); }
    @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
    .animate-marquee { animation: marquee 60s linear infinite; }
    :focus-visible, button:focus-visible, a:focus-visible { outline: 2px solid var(--emerald-500); outline-offset: 2px; }
  `;

  return (
    <>
      <style>{pageStyles}</style>
      <div className="bg-white dark:bg-slate-950">
        <Header setMenuOpen={setMenuOpen} theme={theme} toggleTheme={toggleTheme} onDemoRequest={handleDemoRequest} />
        <MobileMenu isOpen={isMenuOpen} setMenuOpen={setMenuOpen} onDemoRequest={handleDemoRequest} />
        <main>
          <Hero onDemoRequest={handleDemoRequest} />
          <InstitutionsTrustBar />
          <Features />
          <WhiteLabelSection onDemoRequest={handleDemoRequest} />
          <Testimonials />
          <FAQ />
        </main>
        <Footer />
        <StickySignupCTA onDemoRequest={handleDemoRequest} />
        <DemoRequestModal isOpen={isDemoModalOpen} onClose={() => setDemoModalOpen(false)} />
      </div>
    </>
  );
}
